import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Package, Plus, Edit, Trash2, DollarSign, Layers, ShoppingBag,
  Search, RefreshCw, AlertCircle, X, Check, Eye,
  Upload, FileSpreadsheet, FileText, Sparkles, ChevronDown, ChevronUp, Coins,
  Tag, Zap, CheckCircle2, Clock,
} from 'lucide-react';
import productService from '../../api/services/productService';
import { formatPrice, DEFAULT_CURRENCY } from '../../utils/currency';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1').replace('/api/v1', '');
const productImgUrl = (raw) => raw.startsWith('http') ? raw : `${API_BASE}/uploads/products/${raw}`;

export default function ProduitsConnected() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null);

  // ── État Import IA ──────────────────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [importState, setImportState] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const fileInputRef = useRef(null);
  const PREVIEW_PAGE_SIZE = 10;

  // ── Requêtes ─────────────────────────────────────────────────────────────────
  const { data: productsData, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts(),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => productService.getCategories(),
  });

  const { data: couponStats, isLoading: loadingCoupons } = useQuery({
    queryKey: ['product-coupons', detailProduct?._id],
    queryFn: () => productService.getProductCoupons(detailProduct._id),
    enabled: !!detailProduct && detailProduct.type === 'digital',
  });
  const couponMeta = couponStats?.meta || { available: 0, sold: 0, total: 0 };

  const allProducts = (productsData?.success && Array.isArray(productsData.data))
    ? productsData.data
    : [];

  const categories = (categoriesData?.success && Array.isArray(categoriesData.data))
    ? categoriesData.data
    : [];

  // ── Mutation suppression ─────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id) => productService.deleteProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      setShowDeleteConfirm(false);
      setSelectedProduct(null);
    },
    onError: () => alert('Erreur lors de la suppression'),
  });

  // ── Handlers Import IA ─────────────────────────────────────────────────────
  const resetImport = () => {
    setImportState('idle');
    setUploadProgress(0);
    setImportPreview(null);
    setImportResult(null);
    setImportError(null);
    setPreviewPage(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImportFile = useCallback(async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    const validExt = name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.pdf');
    if (!validExt) {
      setImportError('Format non supporté. Déposez un fichier .xlsx, .xls ou .pdf');
      setImportState('error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setImportError('Fichier trop volumineux (max 10 Mo)');
      setImportState('error');
      return;
    }
    setImportError(null);
    setImportState('uploading');
    setUploadProgress(0);
    try {
      setImportState('analyzing');

      let result;
      try {
        result = await productService.previewImport(file, (pct) => setUploadProgress(pct));
      } catch (firstErr) {
        // Retry silently once on server error (cold-start de pdfjs ou erreur transitoire).
        // Les erreurs 4xx (fichier invalide, crédits insuffisants) sont relancées immédiatement.
        const httpStatus = firstErr?._httpStatus ?? 0;
        if (httpStatus >= 500 || httpStatus === 0) {
          await new Promise(r => setTimeout(r, 800));
          result = await productService.previewImport(file, (pct) => setUploadProgress(pct));
        } else {
          throw firstErr;
        }
      }

      if (result.success) {
        setImportPreview(result.data);
        setPreviewPage(0);
        setImportState('preview');
      } else {
        throw new Error(result.message || "Erreur lors de l'analyse");
      }
    } catch (err) {
      const msg = err?.message || err?.error?.message || "Erreur lors de l'analyse du fichier";
      setImportError(msg);
      setImportState('error');
    }
  }, []);

  const handleConfirmImport = async () => {
    if (!importPreview?.products?.length) return;
    setImportState('confirming');
    try {
      const result = await productService.confirmImport(importPreview.products);
      if (result.success) {
        setImportResult(result.data);
        setImportState('done');
        qc.invalidateQueries({ queryKey: ['products'] });
        qc.invalidateQueries({ queryKey: ['categories'] });
      } else {
        throw new Error(result.message || 'Erreur lors de la création');
      }
    } catch (err) {
      const msg = err?.message || err?.error?.message || 'Erreur lors de la création des produits';
      setImportError(msg);
      setImportState('error');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImportFile(file);
  }, [handleImportFile]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleDelete = () => {
    if (!selectedProduct) return;
    deleteMutation.mutate(selectedProduct._id);
  };

  const confirmDelete = (product) => {
    setSelectedProduct(product);
    setShowDeleteConfirm(true);
  };

  // Filtre catégorie côté client (évite un re-fetch par catégorie)
  const productsForCategory = filterCategory === 'all'
    ? allProducts
    : allProducts.filter(p => p.category === filterCategory);

  const filteredProducts = productsForCategory.filter(product => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const totalProducts = filteredProducts.length;
  const totalValue = filteredProducts.reduce((sum, p) => sum + (p.price || 0), 0);
  const uniqueCategories = [...new Set(filteredProducts.map(p => p.category))].length;
  const inStock = filteredProducts.filter(p => p.stock > 0).length;
  const displayCurrency = filteredProducts[0]?.currency || DEFAULT_CURRENCY;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 mx-auto mb-4"
            style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: 'var(--c-border)', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }}
          />
          <p className="text-label">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  if (isError && !productsData) {
    return (
      <div className="card p-5 mb-4" style={{ borderColor: 'rgba(224,84,84,0.3)' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={16} style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>Erreur de chargement</p>
            <p className="text-label mt-1">{error?.message}</p>
            <button onClick={() => qc.invalidateQueries({ queryKey: ['products'] })} className="btn-primary mt-3" style={{ height: '32px', fontSize: '12px' }}>
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>

      {/* ── En-tête de page ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Catalogue
          </p>
          <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>
            Produits &amp; Services
          </h1>
        </div>
        <button className="btn-primary" onClick={() => navigate('/produits/add')}>
          <Plus size={14} strokeWidth={2} />
          Ajouter
        </button>
      </div>

      {/* ── Métriques ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total produits',   value: totalProducts,                              Icon: Package },
          { label: 'Valeur catalogue', value: formatPrice(totalValue, displayCurrency),   Icon: DollarSign },
          { label: 'Catégories',       value: uniqueCategories,                           Icon: Layers },
          { label: 'En stock',         value: inStock,                                    Icon: ShoppingBag },
        ].map(({ label, value, Icon }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.3, ease: 'easeOut' }}
            className="card-metric p-5"
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
              style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
            >
              <Icon size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
            </div>
            <p className="text-label mb-1">{label}</p>
            <p className="font-metric text-ink" style={{ fontSize: '26px', fontWeight: 600, lineHeight: 1 }}>
              {value}
            </p>
          </motion.div>
        ))}
      </div>

      {/* ── Import IA ──────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.28 }}
        className="card mb-5 overflow-hidden"
      >
        <button
          onClick={() => { setImportOpen(o => !o); if (importOpen) resetImport(); }}
          className="w-full flex items-center justify-between p-4 cursor-pointer"
          style={{ background: 'transparent' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-hover)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ background: 'rgba(149,76,233,0.12)' }}
            >
              <Sparkles size={18} style={{ color: '#9B59B6' }} strokeWidth={1.5} />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-ink">Import IA</p>
              <p className="text-label">Importez un catalogue Excel ou PDF — l'IA crée tous vos produits</p>
            </div>
          </div>
          {importOpen
            ? <ChevronUp size={16} style={{ color: 'var(--c-text-muted)' }} strokeWidth={1.5} />
            : <ChevronDown size={16} style={{ color: 'var(--c-text-muted)' }} strokeWidth={1.5} />}
        </button>

        <AnimatePresence>
          {importOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ borderTop: '0.5px solid var(--c-border)' }}
            >
              <div className="p-4">

                {/* Idle : drag & drop */}
                {importState === 'idle' && (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-xl p-8 text-center cursor-pointer"
                    style={{
                      border: `2px dashed ${isDragging ? '#9B59B6' : 'var(--c-border)'}`,
                      background: isDragging ? 'rgba(149,76,233,0.06)' : 'transparent',
                    }}
                    onMouseEnter={e => { if (!isDragging) e.currentTarget.style.borderColor = 'rgba(149,76,233,0.5)'; }}
                    onMouseLeave={e => { if (!isDragging) e.currentTarget.style.borderColor = 'var(--c-border)'; }}
                  >
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.pdf" className="hidden"
                      onChange={(e) => handleImportFile(e.target.files?.[0])} />
                    <Upload size={28} className="mx-auto mb-3" style={{ color: '#9B59B6' }} strokeWidth={1.5} />
                    <p className="text-sm font-medium text-ink mb-1">Déposez votre fichier ici</p>
                    <p className="text-label mb-3">ou cliquez pour parcourir</p>
                    <div className="flex items-center justify-center gap-4">
                      <span className="text-label flex items-center gap-1">
                        <FileSpreadsheet size={13} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} /> Excel (.xlsx, .xls)
                      </span>
                      <span className="text-label flex items-center gap-1">
                        <FileText size={13} style={{ color: 'var(--c-error)' }} /> PDF (.pdf)
                      </span>
                      <span className="text-label">Max 10 Mo</span>
                    </div>
                  </div>
                )}

                {/* Uploading / Analyzing */}
                {(importState === 'uploading' || importState === 'analyzing') && (
                  <div className="text-center py-8">
                    <div className="relative w-14 h-14 mx-auto mb-4">
                      <div className="absolute inset-0 rounded-full" style={{ border: '3px solid rgba(149,76,233,0.15)' }} />
                      <div className="absolute inset-0 rounded-full animate-spin" style={{ border: '3px solid transparent', borderTopColor: '#9B59B6' }} />
                      <Sparkles className="absolute inset-0 m-auto" size={18} style={{ color: '#9B59B6' }} />
                    </div>
                    {importState === 'uploading' ? (
                      <>
                        <p className="text-sm font-medium text-ink mb-2">Envoi du fichier...</p>
                        <div className="w-48 rounded-full h-1.5 mx-auto" style={{ background: 'var(--c-border)' }}>
                          <div className="h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%`, background: '#9B59B6' }} />
                        </div>
                        <p className="text-label mt-1">{uploadProgress}%</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-ink mb-1">L'IA analyse votre fichier...</p>
                        <p className="text-label">Extraction et structuration des produits en cours</p>
                      </>
                    )}
                  </div>
                )}

                {/* Error */}
                {importState === 'error' && (
                  <div className="rounded-xl p-4" style={{ background: 'rgba(224,84,84,0.06)', border: '0.5px solid rgba(224,84,84,0.25)' }}>
                    <div className="flex items-start gap-3 mb-4">
                      <AlertCircle size={18} style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 1 }} strokeWidth={1.5} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>Erreur</p>
                        <p className="text-label mt-0.5">{importError}</p>
                      </div>
                    </div>
                    <button onClick={resetImport} className="btn-ghost" style={{ height: '32px', fontSize: '12px' }}>
                      Réessayer
                    </button>
                  </div>
                )}

                {/* Preview */}
                {importState === 'preview' && importPreview && (
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
                        style={{ background: 'rgba(149,76,233,0.1)', border: '0.5px solid rgba(149,76,233,0.25)', color: '#9B59B6' }}>
                        <Package size={13} strokeWidth={1.5} />
                        <span className="font-medium">{importPreview.count} produits détectés</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs" style={{
                        background: importPreview.canAfford ? 'rgba(79,179,103,0.1)' : 'rgba(224,84,84,0.1)',
                        border: `0.5px solid ${importPreview.canAfford ? 'rgba(79,179,103,0.25)' : 'rgba(224,84,84,0.25)'}`,
                        color: importPreview.canAfford ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-error)',
                      }}>
                        <Coins size={13} strokeWidth={1.5} />
                        <span>{importPreview.estimatedCredits} crédits ({importPreview.currentBalance} disponibles)</span>
                      </div>
                      {!importPreview.canAfford && (
                        <p className="text-xs w-full" style={{ color: 'var(--c-error)' }}>
                          Solde insuffisant. Rechargez votre compte pour importer ces produits.
                        </p>
                      )}
                    </div>

                    {importPreview.count > 0 ? (
                      <>
                        <div className="rounded-lg overflow-hidden mb-3" style={{ border: '0.5px solid var(--c-border)' }}>
                          <table className="w-full text-sm min-w-[500px]">
                            <thead style={{ background: 'var(--c-primary-dark)' }}>
                              <tr>
                                {['#', 'Nom', 'Catégorie', 'Prix', 'Stock', 'SKU'].map(h => (
                                  <th key={h} className="text-left p-3 text-xs font-medium"
                                    style={{ color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importPreview.products
                                .slice(previewPage * PREVIEW_PAGE_SIZE, (previewPage + 1) * PREVIEW_PAGE_SIZE)
                                .map((p, idx) => (
                                  <tr key={idx} style={{ borderTop: '0.5px solid var(--c-border)' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-hover)')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                    <td className="p-3 text-label">{previewPage * PREVIEW_PAGE_SIZE + idx + 1}</td>
                                    <td className="p-3 text-sm font-medium text-ink truncate max-w-[160px]">{p.name}</td>
                                    <td className="p-3">
                                      <span className="px-2 py-0.5 rounded-full text-xs"
                                        style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)', color: 'rgb(var(--c-accent-green-rgb))' }}>
                                        {p.category}
                                      </span>
                                    </td>
                                    <td className="p-3 text-sm text-ink">{formatPrice(p.price, p.currency || 'USD')}</td>
                                    <td className="p-3 text-label">{p.stock}</td>
                                    <td className="p-3 text-label">{p.sku || '—'}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>

                        {importPreview.count > PREVIEW_PAGE_SIZE && (
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-label">
                              {previewPage * PREVIEW_PAGE_SIZE + 1}–{Math.min((previewPage + 1) * PREVIEW_PAGE_SIZE, importPreview.count)} / {importPreview.count}
                            </span>
                            <div className="flex gap-2">
                              {['‹', '›'].map((arrow, di) => (
                                <button key={arrow}
                                  onClick={() => setPreviewPage(p => di === 0 ? Math.max(0, p - 1) : Math.min(Math.ceil(importPreview.count / PREVIEW_PAGE_SIZE) - 1, p + 1))}
                                  disabled={di === 0 ? previewPage === 0 : (previewPage + 1) * PREVIEW_PAGE_SIZE >= importPreview.count}
                                  className="px-3 py-1 rounded-lg text-sm text-ink disabled:opacity-40"
                                  style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)' }}>
                                  {arrow}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-6">
                        <Package size={28} className="mx-auto mb-2" style={{ color: 'var(--c-text-muted)' }} strokeWidth={1.5} />
                        <p className="text-label">Aucun produit détecté dans ce fichier.</p>
                        <p className="text-label mt-1" style={{ fontSize: '11px' }}>Vérifiez que le fichier contient bien des données produits lisibles.</p>
                      </div>
                    )}

                    <div className="flex gap-3 mt-2">
                      <button onClick={resetImport} className="btn-ghost" style={{ height: '34px', fontSize: '12px' }}>
                        Annuler
                      </button>
                      {importPreview.count > 0 && (
                        <button
                          onClick={handleConfirmImport}
                          disabled={!importPreview.canAfford}
                          className="flex-1 sm:flex-none px-5 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: '#9B59B6', color: '#fff' }}
                        >
                          <Sparkles size={14} strokeWidth={1.5} />
                          Créer {importPreview.count} produit{importPreview.count > 1 ? 's' : ''} ({importPreview.estimatedCredits} crédits)
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Confirming */}
                {importState === 'confirming' && (
                  <div className="text-center py-8">
                    <div className="relative w-14 h-14 mx-auto mb-4">
                      <div className="absolute inset-0 rounded-full" style={{ border: '3px solid rgba(var(--c-accent-green-rgb), 0.15)' }} />
                      <div className="absolute inset-0 rounded-full animate-spin" style={{ border: '3px solid transparent', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }} />
                      <Check className="absolute inset-0 m-auto" size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                    </div>
                    <p className="text-sm font-medium text-ink mb-1">Création des produits en cours...</p>
                    <p className="text-label">Veuillez patienter</p>
                  </div>
                )}

                {/* Done */}
                {importState === 'done' && importResult && (
                  <div className="rounded-xl p-4" style={{ background: 'rgba(79,179,103,0.08)', border: '0.5px solid rgba(79,179,103,0.25)' }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(79,179,103,0.15)' }}>
                        <Check size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-ink">Import terminé</p>
                        <p className="text-label">{importResult.creditsDebited} crédits débités</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        <p className="font-metric" style={{ fontSize: '22px', fontWeight: 600, color: 'rgb(var(--c-accent-green-rgb))' }}>{importResult.created}</p>
                        <p className="text-label">Produits créés</p>
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        <p className="font-metric" style={{ fontSize: '22px', fontWeight: 600, color: 'var(--c-warning)' }}>{importResult.failed}</p>
                        <p className="text-label">Ignorés / erreurs</p>
                      </div>
                    </div>
                    {importResult.errors?.length > 0 && (
                      <details className="mb-3">
                        <summary className="text-label cursor-pointer hover:text-ink transition-colors">
                          Voir les {importResult.errors.length} avertissement(s)
                        </summary>
                        <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                          {importResult.errors.map((e, i) => (
                            <li key={i} className="text-xs px-2 py-1 rounded" style={{ color: 'var(--c-warning)', background: 'rgba(229,162,53,0.06)' }}>{e}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <button onClick={() => { resetImport(); setImportOpen(false); }} className="btn-primary" style={{ height: '34px', fontSize: '12px' }}>
                      Fermer
                    </button>
                  </div>
                )}

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Barre de recherche ─────────────────────────────────────────────── */}
      <div className="card p-4 mb-5">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--c-text-muted)' }} />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-sm pl-9 pr-3 py-2 rounded-lg outline-none"
              style={{ background: 'var(--c-primary-dark)', color: 'var(--c-ink)', border: '0.5px solid var(--c-border)' }}
              onFocus={e => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
              onBlur={e => (e.target.style.borderColor = 'var(--c-border)')}
            />
          </div>
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['products'] })}
            className="p-2 rounded-lg"
            style={{ background: 'var(--c-hover)', color: 'var(--c-text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--c-ink)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--c-text-muted)'; }}
          >
            <RefreshCw size={16} strokeWidth={1.5} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Titre liste ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-ink flex items-center gap-2">
          <Package size={15} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
          Liste des produits et services
          {filteredProducts.length > 0 && (
            <span className="menu-badge" style={{ fontSize: '10px', padding: '1px 6px' }}>
              {filteredProducts.length}
            </span>
          )}
        </h2>
      </div>

      {/* ── État vide ──────────────────────────────────────────────────────── */}
      {filteredProducts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="card p-12 text-center"
        >
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.08)' }}>
            <Package size={32} style={{ color: 'var(--c-text-muted)' }} strokeWidth={1.5} />
          </div>
          <h3 className="text-ink font-medium mb-2" style={{ fontSize: '16px' }}>
            {searchQuery ? 'Aucun résultat' : 'Aucun produit'}
          </h3>
          <p className="text-label mb-6 max-w-sm mx-auto">
            {searchQuery ? "Essayez avec d'autres mots-clés" : 'Commencez par ajouter votre premier produit'}
          </p>
          {!searchQuery && (
            <button className="btn-primary" onClick={() => navigate('/produits/add')}>
              <Plus size={14} strokeWidth={2} />
              Ajouter un produit
            </button>
          )}
        </motion.div>
      ) : (
        <>
          {/* ── Tableau desktop ──────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="hidden md:block card overflow-hidden mb-4"
          >
            <div className="grid gap-4 px-5 py-3"
              style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', background: 'var(--c-primary-dark)', borderBottom: '0.5px solid var(--c-border)' }}>
              {['Produit', 'Catégorie', 'Prix', 'Stock', 'Statut', 'Actions'].map((h, i) => (
                <span key={h} className="text-xs font-medium"
                  style={{ color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: i === 5 ? 'right' : 'left' }}>
                  {h}
                </span>
              ))}
            </div>

            {filteredProducts.map((product, index) => (
              <motion.div
                key={product._id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                className="grid gap-4 items-center px-5 py-4"
                style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', borderBottom: '0.5px solid var(--c-border)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                {/* Produit */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', border: '0.5px solid var(--c-border)' }}>
                    {product.images?.[0] ? (
                      <img src={productImgUrl(product.images[0])}
                        alt={product.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Package size={15} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{product.name}</p>
                    {product.type === 'digital' && (
                      <Zap size={12} className="text-yellow-400 flex-shrink-0" title="Produit digital" />
                    )}
                  </div>
                </div>

                {/* Catégorie */}
                <div>
                  {product.category ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium"
                      style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', color: 'rgb(var(--c-accent-green-rgb))', border: '0.5px solid rgba(var(--c-accent-green-rgb), 0.25)' }}>
                      <Tag size={10} strokeWidth={2} />
                      {product.category}
                    </span>
                  ) : <span className="text-label">—</span>}
                </div>

                {/* Prix */}
                <div>
                  <span className="text-sm font-medium text-ink">{formatPrice(product.price, product.currency || DEFAULT_CURRENCY)}</span>
                </div>

                {/* Stock */}
                <div>
                  <span className="text-sm" style={{ color: product.stock > 0 ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-error)' }}>
                    {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
                  </span>
                </div>

                {/* Statut */}
                <div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium"
                    style={{
                      background: product.isActive ? 'rgba(79,179,103,0.1)' : 'rgba(138,138,138,0.1)',
                      color: product.isActive ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-muted)',
                      border: `0.5px solid ${product.isActive ? 'rgba(79,179,103,0.25)' : 'rgba(138,138,138,0.2)'}`,
                    }}>
                    {product.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-0.5 justify-end">
                  <button onClick={() => setDetailProduct(product)} className="p-1.5 rounded-lg cursor-pointer"
                    style={{ color: 'var(--c-text-gray)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-hover)'; e.currentTarget.style.color = 'var(--c-info)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
                    title="Voir">
                    <Eye size={15} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => navigate(`/produits/edit/${product._id}`)} className="p-1.5 rounded-lg cursor-pointer"
                    style={{ color: 'var(--c-text-gray)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-hover)'; e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
                    title="Modifier">
                    <Edit size={15} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => confirmDelete(product)} className="p-1.5 rounded-lg cursor-pointer"
                    style={{ color: 'var(--c-text-gray)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(224,84,84,0.1)'; e.currentTarget.style.color = 'var(--c-error)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
                    title="Supprimer">
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── Cartes mobile ─────────────────────────────────────────────── */}
          <div className="md:hidden space-y-3">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className="card relative overflow-hidden"
                style={{ padding: '16px' }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: 'rgb(var(--c-accent-green-rgb))' }} />
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="text-sm font-medium text-ink truncate">{product.name}</h4>
                      {product.type === 'digital' && (
                        <Zap size={12} className="text-yellow-400 flex-shrink-0" title="Produit digital" />
                      )}
                    </div>
                    {product.category && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs"
                        style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', color: 'rgb(var(--c-accent-green-rgb))' }}>
                        {product.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-ink whitespace-nowrap flex-shrink-0">
                    {formatPrice(product.price, product.currency || DEFAULT_CURRENCY)}
                  </p>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs" style={{ color: product.stock > 0 ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-error)' }}>
                      {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        background: product.isActive ? 'rgba(79,179,103,0.1)' : 'rgba(138,138,138,0.1)',
                        color: product.isActive ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-muted)',
                      }}>
                      {product.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => setDetailProduct(product)} className="p-1.5 rounded-lg cursor-pointer"
                      style={{ color: 'var(--c-text-gray)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--c-info)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--c-text-gray)'; }}>
                      <Eye size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => navigate(`/produits/edit/${product._id}`)} className="p-1.5 rounded-lg cursor-pointer"
                      style={{ color: 'var(--c-text-gray)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--c-text-gray)'; }}>
                      <Edit size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => confirmDelete(product)} className="p-1.5 rounded-lg cursor-pointer"
                      style={{ color: 'var(--c-text-gray)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--c-error)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--c-text-gray)'; }}>
                      <Trash2 size={15} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* ── Modal Détail produit ───────────────────────────────────────────── */}
      <AnimatePresence>
        {detailProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)', zIndex: 'var(--z-modal)' }}
            onClick={() => setDetailProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="glass w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-5" style={{ borderBottom: '0.5px solid var(--c-border)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 rounded-xl flex-shrink-0"
                    style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                    <Package size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-ink font-semibold truncate" style={{ fontSize: '16px' }}>{detailProduct.name}</h3>
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                      {detailProduct.category && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs"
                          style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', color: 'rgb(var(--c-accent-green-rgb))' }}>
                          {detailProduct.category}
                        </span>
                      )}
                      {detailProduct.type === 'digital' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: 'rgba(251,191,36,0.12)', color: '#F59E0B', border: '0.5px solid rgba(251,191,36,0.25)' }}>
                          <Zap size={10} />
                          Digital
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => setDetailProduct(null)} className="p-2 rounded-lg flex-shrink-0 ml-2 cursor-pointer"
                  style={{ color: 'var(--c-text-muted)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-hover)'; e.currentTarget.style.color = 'var(--c-ink)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-muted)'; }}>
                  <X size={16} strokeWidth={1.5} />
                </button>
              </div>

              {/* Images */}
              {detailProduct.images?.length > 0 && (
                <div className="flex gap-2 p-4 pb-0 overflow-x-auto">
                  {detailProduct.images.map((img, i) => (
                    <img key={i}
                      src={productImgUrl(img)}
                      alt={detailProduct.name}
                      className="w-24 h-24 object-cover rounded-xl flex-shrink-0"
                      style={{ border: '0.5px solid var(--c-border)' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  ))}
                </div>
              )}

              <div className="p-5 space-y-4">
                {/* Prix & Stock */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Prix', value: formatPrice(detailProduct.price, detailProduct.currency || DEFAULT_CURRENCY), color: 'var(--c-ink)' },
                    { label: 'Stock', value: detailProduct.stock > 0 ? detailProduct.stock : 'Rupture', color: detailProduct.stock > 0 ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-error)' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="rounded-xl p-3"
                      style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                      <p className="text-label mb-1">{label}</p>
                      <p className="font-metric font-semibold" style={{ fontSize: '18px', color }}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Coupons digitaux */}
                {detailProduct.type === 'digital' && (
                  <div className="rounded-xl overflow-hidden" style={{ border: '0.5px solid var(--c-border)' }}>
                    <div className="px-3 py-2 flex items-center gap-2"
                      style={{ background: 'rgba(251,191,36,0.06)', borderBottom: '0.5px solid var(--c-border)' }}>
                      <Zap size={14} className="text-yellow-400" />
                      <span className="text-xs font-medium text-ink">Coupons digitaux</span>
                    </div>
                    <div className="grid grid-cols-2">
                      <div className="p-4 text-center" style={{ borderRight: '0.5px solid var(--c-border)' }}>
                        {loadingCoupons ? (
                          <div className="h-7 w-10 mx-auto animate-pulse rounded" style={{ background: 'var(--c-hover)' }} />
                        ) : (
                          <p className="text-2xl font-metric font-bold" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                            {couponMeta.available}
                          </p>
                        )}
                        <p className="text-xs text-label mt-0.5 flex items-center justify-center gap-1">
                          <Clock size={11} />
                          Disponibles
                        </p>
                      </div>
                      <div className="p-4 text-center">
                        {loadingCoupons ? (
                          <div className="h-7 w-10 mx-auto animate-pulse rounded" style={{ background: 'var(--c-hover)' }} />
                        ) : (
                          <p className="text-2xl font-metric font-bold text-blue-400">
                            {couponMeta.sold}
                          </p>
                        )}
                        <p className="text-xs text-label mt-0.5 flex items-center justify-center gap-1">
                          <CheckCircle2 size={11} />
                          Livrés
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description */}
                {detailProduct.description && (
                  <div>
                    <p className="text-label mb-1.5">Description</p>
                    <p className="text-sm text-ink leading-relaxed">{detailProduct.description}</p>
                  </div>
                )}

                {/* SKU & Statut */}
                <div className="flex flex-wrap gap-3">
                  {detailProduct.sku && (
                    <div>
                      <p className="text-label mb-1">SKU</p>
                      <p className="font-mono text-sm text-ink px-3 py-1.5 rounded-lg"
                        style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        {detailProduct.sku}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-label mb-1">Statut</p>
                    <span className="inline-block px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{
                        background: detailProduct.isActive ? 'rgba(79,179,103,0.1)' : 'rgba(138,138,138,0.1)',
                        color: detailProduct.isActive ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-muted)',
                        border: `0.5px solid ${detailProduct.isActive ? 'rgba(79,179,103,0.25)' : 'rgba(138,138,138,0.2)'}`,
                      }}>
                      {detailProduct.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                {detailProduct.tags?.length > 0 && (
                  <div>
                    <p className="text-label mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {detailProduct.tags.map((tag, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full text-xs text-label"
                          style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variantes */}
                {detailProduct.variants?.length > 0 && (
                  <div>
                    <p className="text-label mb-2">Variantes ({detailProduct.variants.length})</p>
                    <div className="space-y-2">
                      {detailProduct.variants.map((v, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg text-sm"
                          style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                          <span className="text-ink">{v.name}</span>
                          <div className="flex gap-3">
                            {v.price != null && <span className="text-label">{formatPrice(v.price, detailProduct.currency || DEFAULT_CURRENCY)}</span>}
                            {v.stock != null && <span className="text-label">Stock : {v.stock}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex gap-3 p-5 pt-0">
                <button
                  onClick={() => { setDetailProduct(null); navigate(`/produits/edit/${detailProduct._id}`); }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', color: 'rgb(var(--c-accent-green-rgb))', border: '0.5px solid rgba(var(--c-accent-green-rgb), 0.25)' }}>
                  <Edit size={15} strokeWidth={1.5} />
                  Modifier
                </button>
                <button
                  onClick={() => { setDetailProduct(null); confirmDelete(detailProduct); }}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer"
                  style={{ background: 'rgba(224,84,84,0.1)', color: 'var(--c-error)', border: '0.5px solid rgba(224,84,84,0.25)' }}>
                  <Trash2 size={15} strokeWidth={1.5} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal confirmation suppression ────────────────────────────────── */}
      <AnimatePresence>
        {showDeleteConfirm && selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)', zIndex: 'var(--z-modal)' }}
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="glass max-w-sm w-full rounded-xl p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(224,84,84,0.12)' }}>
                  <AlertCircle size={18} style={{ color: 'var(--c-error)' }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink">Supprimer le produit ?</p>
                  <p className="text-label">Cette action est irréversible</p>
                </div>
              </div>

              <div className="rounded-lg p-4 mb-5"
                style={{ background: 'rgba(224,84,84,0.06)', border: '0.5px solid rgba(224,84,84,0.2)' }}>
                <p className="text-sm text-ink">
                  Vous allez supprimer <strong>{selectedProduct.name}</strong>.
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-ink cursor-pointer"
                  style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)' }}>
                  Annuler
                </button>
                <button onClick={handleDelete}
                  className="flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'var(--c-error)', color: '#fff' }}>
                  <Trash2 size={14} strokeWidth={1.5} />
                  Supprimer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
